<template>
    <div class="enterprise-detail">
        <achiTitle title="合作科研机构一览" subtitle="以合作促发展 以合作促创新"></achiTitle>

        <!-- 加载状态 -->
        <el-loading v-if="loading" fullscreen text="正在加载企业详情..."></el-loading>

        <!-- 错误提示 -->
        <el-alert v-if="error" title="数据加载失败" :description="error" type="error" show-icon
            class="error-alert"></el-alert>

        <!-- 企业详情内容 -->
        <div v-if="enterprise && !loading && !error" class="detail-container">
            <!-- 头部信息 -->
            <div class="header-section">
                <div class="logo-container">
                    <img :src="enterprise.logo" :alt="enterprise.name + 'logo'" class="logo-img"
                        @error="handleImageError">
                </div>
                <div class="info-header">
                    <h1 class="enterprise-title">{{ enterprise.name }}</h1>
                    <div class="basic-info">
                        <div class="info-item">
                            <i class="el-icon-map-marker"></i>
                            <span>单位地址: {{ enterprise.address }}</span>
                        </div>
                        <div class="info-item">
                            <i class="el-icon-phone"></i>
                            <span>联系电话：{{ enterprise.phone }}</span>
                        </div>
                        <div class="info-item">
                            <i class="el-icon-document"></i>
                            <span>成果总数: {{ enterprise.achievements.length }}</span>
                        </div>
                        <div class="info-item">
                            <i class="el-icon-user"></i>
                            <span>专家人数: {{ enterprise.experts.length }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 企业介绍 -->
            <div class="introduction-section">
                <h2 class="section-title">高校/机构介绍</h2>
                <div class="introduction-content">
                    <p v-for="(paragraph, index) in introductionParagraphs" :key="index">
                        {{ paragraph }}
                    </p>
                </div>
            </div>

            <!-- 科研成果 -->
            <div class="achievements-section" v-if="enterprise.achievements && enterprise.achievements.length > 0">
                <h2 class="section-title">科研成果</h2>
                <el-list>
                    <el-list-item v-for="achievement in enterprise.achievements" :key="achievement.id">
                        <achievementItem :achievement="achievement"></achievementItem>
             
                    </el-list-item>
                </el-list>
            </div>

            <!-- 技术专家 -->
            <div class="experts-section" v-if="enterprise.technicalExperts && enterprise.technicalExperts.length > 0">
                <h2 class="section-title">技术专家</h2>
                <el-row :gutter="20">
                    <el-col :xs="24" :sm="12" :md="8" v-for="expert in enterprise.technicalExperts" :key="expert.id">
                        <expertItem :expert="expert"/>
                    </el-col>
                </el-row>
            </div>
        </div>
    </div>
</template>

<script>
import expertItem from './expertItem.vue';
import achievementItem from './achievementItem.vue';
export default {
    props: {
        id: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            enterprise: null,
            loading: true,
            error: null,
            allEnterprises: [],
            allAchievements: [],
            allExperts: []
        };
    },
    components: { expertItem, achievementItem },
    computed: {
        // 将介绍文本按换行符分割成段落
        introductionParagraphs() {
            if (!this.enterprise || !this.enterprise.introduction) return [];
            return this.enterprise.introduction.split('\n').filter(p => p.trim() !== '');
        }
    },
    mounted() {
        this.loadEnterpriseDetail(this.id);
    },
    methods: {
        async loadEnterpriseDetail(id) {
            try {
                this.loading = true;
                // 加载企业数据
                const enterpriseResponse = await this.$httpTemp.get('/mock/enterprises.json');
                this.allEnterprises = enterpriseResponse;

                // 加载成果数据
                const achievementResponse = await this.$httpTemp.get('/mock/achievement.json');
                this.allAchievements = achievementResponse.achievements;

                // 加载专家数据
                const expertResponse = await this.$httpTemp.get('/mock/technical_experts.json');
                this.allExperts = expertResponse;

                // 查找匹配ID的企业
                const foundEnterprise = this.allEnterprises.find(item => item.id === this.id);
                if (foundEnterprise) {
                    // 关联成果数据
                    foundEnterprise.achievements = foundEnterprise.achievements.map(id =>
                        this.allAchievements.find(ach => ach.id === id)
                    ).filter(Boolean);

                    // 关联专家数据
                    foundEnterprise.technicalExperts = foundEnterprise.experts.map(id =>
                        this.allExperts.find(exp => exp.id === id)
                    ).filter(Boolean);

                    this.enterprise = foundEnterprise;
                    this.error = null;
                } else {
                    this.error = '未找到指定的企业信息';
                }
            } catch (err) {
                this.error = '数据加载失败，请稍后重试';
                console.error('加载企业详情出错:', err);
            } finally {
                this.loading = false;
            }
        },
        handleImageError(e) {
            // logo加载失败时使用默认图片
            e.target.src = 'https://picsum.photos/200/200?grayscale';
        }
    }
};
</script>

<style scoped>
.enterprise-detail {
    max-width: 1200px;
    margin: 0 auto;
    padding: 30px;
}

.error-alert {
    margin-bottom: 20px;
}

.detail-container {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    padding: 30px;
}

.header-section {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    flex-wrap: wrap;
    gap: 20px;
}

.logo-container {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    overflow: hidden;
    border: 4px solid #f5f5f5;
    flex-shrink: 0;
}

.logo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.info-header {
    flex-grow: 1;
}

.enterprise-title {
    margin: 0 0 20px 0;
    color: #333;
    font-size: 28px;
    font-weight: 700;
}

.basic-info {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    color: #666;
}

.info-item {
    display: flex;
    align-items: center;
    font-size: 16px;
}

.info-item i {
    margin-right: 8px;
    color: #409eff;
}

.section-title {
    font-size: 22px;
    color: #333;
    margin: 30px 0 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #f0f0f0;
}

.introduction-content {
    color: #666;
    line-height: 1.8;
    font-size: 16px;
}

.introduction-content p {
    margin: 0 0 15px 0;
}

.achievement-item {
    padding: 15px 0;
    border-bottom: 1px dashed #eee;
}

.achievement-item h3 {
    margin: 0 0 10px 0;
    color: #333;
}

.achievement-item p {
    color: #666;
    line-height: 1.6;
}

.achievement-meta {
    margin-top: 10px;
    display: flex;
    gap: 20px;
    color: #999;
    font-size: 14px;
}

.expert-info {
    display: flex;
    align-items: center;
    padding: 10px;
}

.expert-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    margin-right: 15px;
    object-fit: cover;
}

.expert-details h3 {
    margin: 0 0 5px 0;
    font-size: 16px;
}

.expert-details p {
    margin: 0;
    color: #666;
    font-size: 14px;
}
</style>
